import priceDataStore, { IPriceDataItem } from "./priceDataStore";
import { IPriceSummaryModel } from "@/lib/interfaces and Enums/InterfacePriceSummary";
import { IBondSecurity } from "@/lib/interfaces and Enums/ENUMBondSecurity";
import bondSecuritiesStore from "./bondSecuritiesStore";
import { getAllBondSecurities } from "./bondSecuritiesStoreACTIONS";

export async function updateSinglePriceInStore(priceModel: IPriceSummaryModel) {
  priceDataStore.getState().updateSinglePrice(priceModel);
}

// ----- LQC-2575: rows from the bond reference data ---------------------------

// EOD publishes all three dates together, so one "as at" date is enough for the row.
export function returnBondPriceDate(bond: IBondSecurity): string | undefined {
  return (
    bond.midPriceDate ?? bond.bidPriceDate ?? bond.askPriceDate ?? undefined
  );
}

// One price row per bond from GET /Securities/bonds. A live MegaPipe price already in
// the store wins over the static EOD one, so a re-seed never rolls a row backwards.
export function mapBondSecuritiesToPriceRows(
  bonds: IBondSecurity[],
  existingRows: IPriceDataItem[] = [],
): IPriceDataItem[] {
  const liveRowsByIsin = new Map(existingRows.map((row) => [row.isin, row]));
  const rowsByIsin = new Map<string, IPriceDataItem>();

  bonds.forEach((bond) => {
    if (!bond.isin) return; // AG Grid keys every price row on the ISIN.
    const liveRow = liveRowsByIsin.get(bond.isin);

    rowsByIsin.set(bond.isin, {
      ...liveRow,
      id: bond.isin,
      isin: bond.isin,
      securityName: bond.securityName,
      issuer: bond.issuer,
      currency: bond.currency,
      maturityDate: bond.maturityDate,
      couponRate: bond.couponRate,
      notation: bond.notation,
      priceDate: returnBondPriceDate(bond),
      bidPrice: liveRow?.bidPrice ?? bond.bidPrice ?? undefined,
      askPrice: liveRow?.askPrice ?? bond.askPrice ?? undefined,
      midPrice: liveRow?.midPrice ?? bond.midPrice ?? undefined,
      // Yield columns read bid / mid / ask (IPriceSummaryModel). The bond payload only
      // fills them once sp_GetBondsWithPrices returns yields - blank until then.
      bid: liveRow?.bid ?? bond.bidYield ?? undefined,
      mid: liveRow?.mid ?? bond.midYield ?? undefined,
      ask: liveRow?.ask ?? bond.askYield ?? undefined,
    });
  });

  return [...rowsByIsin.values()];
}

export function setPriceRowsFromBondSecurities(bonds: IBondSecurity[]) {
  const currentRows = priceDataStore.getState().data;
  priceDataStore
    .getState()
    .setData(mapBondSecuritiesToPriceRows(bonds, currentRows));
}

// The Prices widget has no loader of its own - it seeds its rows from the same bond
// reference data the Leg Creation widget reads. getAllBondSecurities is
// fetch-once and swallows its own failures, so every widget instance can call this:
// whichever widget mounts first pays for the request, the rest reuse the store.
export async function getPricesFromBondSecuritiesAndAddToStore(
  getAccessTokenSilently: Function,
) {
  await getAllBondSecurities(getAccessTokenSilently);
  setPriceRowsFromBondSecurities(bondSecuritiesStore.getState().bondSecurities);
}
